import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export interface BusinessExpense {
  id: string;
  property_id?: string;
  expenses_type?: "Reembolso" | "Nómina" | "Extras" | "Otros";
  expense_description?: string;
  payment_type:
    | "Efectivo"
    | "Transferencia"
    | "Tarjeta de Crédito"
    | "Tarjeta de Débito"
    | "Deducción";
  total_payment: number;
  payment_date?: string;
  expense_type_id?: string;
  expense_subtype_id?: string;
  receipt_url?: string;
  created_at: string;
  updated_at: string;
  properties?: {
    title: string;
    short_name: string;
  };
  business_expenses_types?: {
    name: string;
  };
  business_expenses_subtypes?: {
    name: string;
  };
}

export interface BusinessExpenseFilters {
  propertyId?: string;
  expenseType?: string;
  startDate?: string;
  endDate?: string;
}

// Helper function to normalize strings for comparison (remove accents, lowercase)
const normalizeString = (str: string): string => {
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
};

export const useBusinessExpenses = (filters?: BusinessExpenseFilters) => {
  return useQuery({
    queryKey: ["business-expenses", filters],
    queryFn: async () => {
      let query = supabase
        .from("business_expenses")
        .select(
          `
          *,
          properties(title, short_name),
          business_expenses_types(name),
          business_expenses_subtypes(name)
        `,
        )
        .order("payment_date", { ascending: false, nullsFirst: false })
        .order("created_at", { ascending: false });

      if (filters?.propertyId && filters.propertyId !== "all") {
        query = query.eq("property_id", filters.propertyId);
      }

      if (filters?.startDate) {
        query = query.gte("payment_date", filters.startDate);
      }

      if (filters?.endDate) {
        query = query.lte("payment_date", filters.endDate);
      }

      const { data, error } = await query;

      if (error) {
        console.error("Error fetching business expenses:", error);
        throw error;
      }

      // Filter by expense type using normalized comparison (handles accents)
      let filteredData = data || [];
      if (filters?.expenseType && filters.expenseType !== "all") {
        const normalizedFilterType = normalizeString(filters.expenseType);
        filteredData = filteredData.filter((item: any) => {
          const itemTypeName = item.business_expenses_types?.name || "";
          return normalizeString(itemTypeName) === normalizedFilterType;
        });
      }

      return filteredData.map((item) => ({
        ...item,
        expenses_type: item.expenses_type as BusinessExpense["expenses_type"],
        payment_type: item.payment_type as BusinessExpense["payment_type"],
      }));
    },
  });
};

export interface CreateBusinessExpenseData {
  organization_id?: string;
  property_id?: string;
  expense_type_id: string;
  expense_subtype_id: string;
  expense_description?: string;
  payment_type:
    | "Efectivo"
    | "Transferencia"
    | "Tarjeta de Crédito"
    | "Tarjeta de Débito"
    | "Deducción";
  total_payment: number;
  payment_date?: string;
  receipt_url?: string;
}

export const useCreateBusinessExpense = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (expense: CreateBusinessExpenseData) => {
      const { data, error } = await supabase
        .from("business_expenses")
        .insert({
          ...expense,
          expenses_type: "Otros",
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["business-expenses"], refetchType: "all" });
      queryClient.invalidateQueries({ queryKey: ["business-expenses-paginated"], refetchType: "all" });
      toast({
        title: "Éxito",
        description: "Egreso creado correctamente",
      });
    },
    onError: (error) => {
      console.error("Error creating business expense:", error);
      toast({
        title: "Error",
        description: "Error al crear el egreso",
        variant: "destructive",
      });
    },
  });
};

export const useUpdateBusinessExpense = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      id,
      ...updates
    }: Partial<BusinessExpense> & { id: string }) => {
      const { data, error } = await supabase
        .from("business_expenses")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["business-expenses"], refetchType: "all" });
      queryClient.invalidateQueries({ queryKey: ["business-expenses-paginated"], refetchType: "all" });
      toast({
        title: "Éxito",
        description: "Egreso actualizado correctamente",
      });
    },
    onError: (error) => {
      console.error("Error updating business expense:", error);
      toast({
        title: "Error",
        description: "Error al actualizar el egreso",
        variant: "destructive",
      });
    },
  });
};

export const useDeleteBusinessExpense = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("business_expenses")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["business-expenses"], refetchType: "all" });
      queryClient.invalidateQueries({ queryKey: ["business-expenses-paginated"], refetchType: "all" });
      toast({
        title: "Éxito",
        description: "Egreso eliminado correctamente",
      });
    },
    onError: (error) => {
      console.error("Error deleting business expense:", error);
      toast({
        title: "Error",
        description: "Error al eliminar el egreso",
        variant: "destructive",
      });
    },
  });
};
